import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  resourceKinds,
  surfaceTypes,
  type ResourceKind,
  type ResourceView,
  type SupportedLocale,
  type SurfaceType,
  type VenueView,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { resourceKindKeys, surfaceKeys, wholeNumber } from './scheduling';

export interface VenuesSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
  /** Whether the person may create venues and resources; the server enforces it as well. */
  canManage: boolean;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

/** Resources of one venue in tree order with their depth, so zones appear under their rink. */
export function treeOrder(
  resources: readonly ResourceView[],
): { resource: ResourceView; depth: number }[] {
  const ordered: { resource: ResourceView; depth: number }[] = [];
  const visit = (parentId: string | null, depth: number): void => {
    for (const resource of resources.filter(
      (candidate) => candidate.parentResourceId === parentId,
    )) {
      ordered.push({ resource, depth });
      visit(resource.id, depth + 1);
    }
  };
  visit(null, 0);
  return ordered;
}

export function VenuesSection({
  locale,
  base,
  csrfToken,
  canManage,
}: VenuesSectionProps): React.JSX.Element {
  const [venues, setVenues] = useState<VenueView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [venueName, setVenueName] = useState('');
  const [venueId, setVenueId] = useState('');
  const [resourceName, setResourceName] = useState('');
  const [parentId, setParentId] = useState('');
  const [kind, setKind] = useState<ResourceKind>('rink');
  const [surface, setSurface] = useState<SurfaceType | ''>('ice');
  const [capacity, setCapacity] = useState('');
  const [setup, setSetup] = useState('0');
  const [cleanup, setCleanup] = useState('0');

  const load = useCallback(async () => {
    try {
      setVenues(await apiRequest<VenueView[]>(`${base}/venues`));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const chosenVenue = venues?.find((venue) => venue.id === venueId) ?? venues?.[0];

  async function addVenue(): Promise<void> {
    setErrorKey(null);
    try {
      await apiRequest<VenueView>(`${base}/venues`, {
        method: 'POST',
        body: { name: venueName },
        csrfToken,
      });
      setVenueName('');
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function addResource(): Promise<void> {
    if (chosenVenue === undefined) return;
    setErrorKey(null);
    try {
      await apiRequest<ResourceView>(`${base}/resources`, {
        method: 'POST',
        body: {
          venueId: chosenVenue.id,
          parentResourceId: parentId === '' ? null : parentId,
          name: resourceName,
          kind,
          surfaceType: surface === '' ? null : surface,
          capacity: wholeNumber(capacity),
          setupBufferMinutes: wholeNumber(setup) ?? 0,
          cleanupBufferMinutes: wholeNumber(cleanup) ?? 0,
        },
        csrfToken,
      });
      setResourceName('');
      setParentId('');
      setCapacity('');
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-label={translate(locale, 'calendar.venues.title')} sx={{ mt: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {translate(locale, 'calendar.venues.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        {translate(locale, 'calendar.venues.intro')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {venues === null && errorKey === null && (
        <Typography>{translate(locale, 'calendar.loading')}</Typography>
      )}
      {venues?.length === 0 && (
        <Typography color="text.secondary">{translate(locale, 'calendar.venues.empty')}</Typography>
      )}
      <Stack spacing={1.5}>
        {venues?.map((venue) => (
          <Box key={venue.id}>
            <Typography sx={{ fontWeight: 600 }}>{venue.name}</Typography>
            <Stack component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {treeOrder(venue.resources).map(({ resource, depth }) => (
                <Typography key={resource.id} component="li" variant="body2" sx={{ pl: depth * 3 }}>
                  {resource.name} · {translate(locale, resourceKindKeys[resource.kind])}
                  {resource.surfaceType === null
                    ? ''
                    : ` · ${translate(locale, surfaceKeys[resource.surfaceType])}`}
                  {resource.capacity === null ? '' : ` · ${String(resource.capacity)}`}
                  {resource.setupBufferMinutes + resource.cleanupBufferMinutes === 0
                    ? ''
                    : ` · +${String(resource.setupBufferMinutes)}/+${String(resource.cleanupBufferMinutes)} min`}
                </Typography>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
      {canManage && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box
            component="form"
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
            onSubmit={(event) => {
              event.preventDefault();
              void addVenue();
            }}
          >
            <TextField
              size="small"
              required
              label={translate(locale, 'calendar.venue.name')}
              value={venueName}
              onChange={(event) => {
                setVenueName(event.target.value);
              }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{ minHeight: 40 }}
              disabled={venueName.trim() === ''}
            >
              {translate(locale, 'calendar.venue.add')}
            </Button>
          </Box>
          {venues !== null && venues.length > 0 && (
            <Box
              component="form"
              sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
              onSubmit={(event) => {
                event.preventDefault();
                void addResource();
              }}
            >
              <TextField
                select
                size="small"
                sx={{ minWidth: 180 }}
                label={translate(locale, 'calendar.venues.title')}
                value={chosenVenue?.id ?? ''}
                onChange={(event) => {
                  setVenueId(event.target.value);
                  setParentId('');
                }}
              >
                {venues.map((venue) => (
                  <MenuItem key={venue.id} value={venue.id}>
                    {venue.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                required
                label={translate(locale, 'calendar.resource.name')}
                value={resourceName}
                onChange={(event) => {
                  setResourceName(event.target.value);
                }}
              />
              <TextField
                select
                size="small"
                sx={{ minWidth: 140 }}
                label={translate(locale, 'calendar.resource.kind')}
                value={kind}
                onChange={(event) => {
                  setKind(event.target.value as ResourceKind);
                }}
              >
                {resourceKinds.map((candidate) => (
                  <MenuItem key={candidate} value={candidate}>
                    {translate(locale, resourceKindKeys[candidate])}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                sx={{ minWidth: 160 }}
                label={translate(locale, 'calendar.resource.parent')}
                value={parentId}
                onChange={(event) => {
                  setParentId(event.target.value);
                }}
              >
                <MenuItem value="">{translate(locale, 'calendar.resource.none')}</MenuItem>
                {chosenVenue?.resources.map((resource) => (
                  <MenuItem key={resource.id} value={resource.id}>
                    {resource.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                sx={{ minWidth: 130 }}
                label={translate(locale, 'calendar.resource.surface')}
                value={surface}
                onChange={(event) => {
                  setSurface(event.target.value as SurfaceType | '');
                }}
              >
                <MenuItem value="">{translate(locale, 'calendar.filter.all')}</MenuItem>
                {surfaceTypes.map((candidate) => (
                  <MenuItem key={candidate} value={candidate}>
                    {translate(locale, surfaceKeys[candidate])}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                sx={{ width: 150 }}
                label={translate(locale, 'calendar.field.capacity')}
                value={capacity}
                onChange={(event) => {
                  setCapacity(event.target.value);
                }}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <TextField
                size="small"
                sx={{ width: 170 }}
                label={translate(locale, 'calendar.resource.setup')}
                value={setup}
                onChange={(event) => {
                  setSetup(event.target.value);
                }}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <TextField
                size="small"
                sx={{ width: 170 }}
                label={translate(locale, 'calendar.resource.cleanup')}
                value={cleanup}
                onChange={(event) => {
                  setCleanup(event.target.value);
                }}
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <Button
                type="submit"
                variant="contained"
                sx={{ minHeight: 40 }}
                disabled={
                  resourceName.trim() === '' ||
                  (capacity.trim() !== '' && wholeNumber(capacity) === null) ||
                  wholeNumber(setup) === null ||
                  wholeNumber(cleanup) === null
                }
              >
                {translate(locale, 'calendar.resource.add')}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
